/**
 *  bridge/index.js
 *  @description: puente entre las rutas de la API vieja (Koa) y la API v1.
 *
 *  Los componentes siguen llamando a Request().get('config/get/areas') como
 *  siempre; httpClient manda cada llamada aquí y el handler de esa ruta llama
 *  a v1 y regresa la respuesta con la forma exacta que el componente espera
 *  ({areas:[…]}, {response:true}, {data:[…]}, {error, message}…).
 *
 *  Así ninguna llamada llega a Koa y los componentes no se tocan; la forma
 *  vieja se queda solo hasta el rediseño de la web.
 */
import catalogs from './catalogs';
import projects from './projects';
import users from './users';
import requests from './requests';
import documents from './documents';
import notifications from './notifications';
import reports from './reports';
import costs from './costs';

const modules = [catalogs, projects, users, requests, documents, notifications, reports, costs];

// Cada módulo exporta { 'GET config/get/areas': handler, ... }. Las rutas con
// parámetros usan ':x' (p. ej. 'DELETE costos/materiales/:x'); el handler
// recibe los valores en `params`.
const routes = [];
modules.forEach((m) =>
   Object.entries(m).forEach(([key, handler]) => {
      const [method, path] = key.split(' ');
      const parts = path.split('/');
      routes.push({ method, parts, handler });
   })
);

function match(method, url) {
   const path = url.split('?')[0].replace(/^\/+|\/+$/g, '');
   const parts = path.split('/');
   for (const r of routes) {
      if (r.method !== method || r.parts.length !== parts.length) continue;
      const params = [];
      const ok = r.parts.every((p, i) => {
         if (p === ':x') {
            params.push(decodeURIComponent(parts[i]));
            return true;
         }
         return p === parts[i];
      });
      if (ok) return { handler: r.handler, params };
   }
   return null;
}

// hasRoute dice si hay handler para la ruta (lo usa el test de cobertura).
export const hasRoute = (method, url) => !!match(method, url);

// handle nunca lanza: los componentes esperan {error, message} en los fallos.
export async function handle(method, url, data, files) {
   const route = match(method, url);
   if (!route) {
      // Si aparece esto, a esa pantalla le falta su handler en el puente.
      console.error(`[bridge] ruta sin migrar a v1: ${method} ${url}`);
      return { error: true, message: `Función no disponible (${method} ${url})` };
   }
   try {
      return await route.handler(data || {}, { params: route.params, files: files || [], url });
   } catch (err) {
      console.error(`[bridge] ${method} ${url}`, err);
      return { error: true, message: err.message || 'Ocurrió un error, intenta de nuevo.' };
   }
}

// --- utilidades para los handlers ---

// fail convierte una respuesta v1 fallida a la forma vieja.
export const fail = (res) => ({ error: true, message: res.message || 'Ocurrió un error, intenta de nuevo.' });

// list regresa {data:[…]} como selectRequest de Koa, o {empty:true} si no hay
// filas (varias pantallas revisan `empty`).
export const list = (rows) => (rows && rows.length ? { data: rows } : { empty: true, data: [] });

// done es la respuesta de updateRequest de Koa.
export const done = (res) => (res.ok ? { response: true } : fail(res));

// Fechas: v1 manda RFC 3339 / YYYY-MM-DD; Koa mandaba lo que regresaba el SP.
export const ymd = (v) => (v ? String(v).slice(0, 10) : null);
